module Client.MeterReadings
open Client.Services
open Elmish
open Fable.Core
open Fable.Core.JsInterop
open Fetch.Types
open Shared
open System
open Thoth.Json
open Feliz
open Feliz.UseElmish
open Feliz.MaterialUI
open Fable.MaterialUI.Icons
open Fable.MaterialUI.MaterialDesignIcons
open Thoth.Fetch
open Fable.Core.Experimental
open Feliz.SweetAlert
open Fable.Core.JS
open Components.MeterReadingsForm
open CustomerDexImaging.Models

let strOption (s : string) = if String.IsNullOrWhiteSpace s then None else Some s

let parseInt (s : string) =
  if String.IsNullOrWhiteSpace s
  then None
  else
    match Int32.TryParse s with
    | false, _ -> None
    | true, v -> Some v

type UpdatedReadingFromUi =
  { MeterId : int
    Reading : int option }

type Msg =
  | EquipmentLookupResult of Result<Equipment,Components.EquipmentLookup.EquipmentLookupFailureResult>
  | EquipmentSelected of Shared.Equipment
  | ContactEntryCompleted of ContactSelectionInfo
  | LoadingMetersResult of Result<Shared.EquipmentMeter list,string>
  | UpdateReading of UpdatedReadingFromUi
  | UpdateSendConfirmationEmailFlag of bool
  | SubmitMeterReadings
  | MeterReadingEntryResult of Result<string,string>
  | ResetState

type ContactEntryState =
  { Equipment : Shared.Equipment
    Contact : ContactSelectionInfo option }

type LoadingMetersState =
  { Equipment : Shared.Equipment
    Contact : ContactSelectionInfo }

type ReadingEntryState =
  { MeterId : int
    MeterType : string
    Reading : int option
    LastReading : int option
    LastReadingDate : DateTime option }

type MeterReadingEntryState =
  { Equipment : Shared.Equipment
    Contact : ContactSelectionInfo
    SendConfirmationEmail : bool
    MeterReadings : ReadingEntryState list }

type SuccessResultState =
  { SuccessMessage : string
    MeterReadingEntryState : MeterReadingEntryState }

type Model =
  | EquipmentLookup
  | ContactEntry of ContactEntryState
  | LoadingMeters of LoadingMetersState
  | MeterReadingEntry of MeterReadingEntryState
  | SubmittingReadings of MeterReadingEntryState
  | ReadingRequestResult of SuccessResultState
  | DowngradedForm of Components.MeterReadingsForm.EntryState

let getMeters (token : string) : Promise<Shared.EquipmentMeter list> =
  promise {
    let decoder = Services.decoder<Shared.EquipmentMeter list>
    let authHeader = HttpRequestHeaders.Authorization <| sprintf "Bearer %s" token
    return! Fetch.get<_,Shared.EquipmentMeter list>("/api/Meters", decoder = decoder,headers = [authHeader] )
  }

let getMetersCmd token =
  Cmd.OfPromise.either getMeters token
    (Ok >> LoadingMetersResult)
    (fun x ->
      "We are currently unable to get meters for the specified equipment to add a reading." |> Error |> LoadingMetersResult)

let submitMeterReadings (token, (request : Shared.NewMeterReadingRequests)) : Promise<string> =
  promise {
    let decoder = Services.decoder<string>
    let authHeader = HttpRequestHeaders.Authorization <| sprintf "Bearer %s" token
    return! Fetch.post<_,string>("/api/MeterReadings", request, decoder = decoder,headers = [authHeader] )
  }

let submitMeterReadingsCmd token request =
  Cmd.OfPromise.either submitMeterReadings (token, request)
    (Ok >> MeterReadingEntryResult)
    (fun x ->
      "Unable to submit readings for equipment. Please retry" |> Error |> MeterReadingEntryResult)

let init (equipmentOption) : Model * Cmd<Msg> =
  let cmd =
    match equipmentOption with
    | Some e ->
      (Ok e) |> EquipmentLookupResult |> Cmd.ofMsg
    | None ->
      Cmd.none
  EquipmentLookup, cmd

let update (msg : Msg) (model : Model) : Model * Cmd<Msg> =
  match model, msg with
  | _, ResetState ->
    EquipmentLookup, Cmd.none
  | EquipmentLookup, EquipmentLookupResult r ->
    match r with
    | Ok e -> model, Cmd.ofMsg (EquipmentSelected e)
    | Error v ->
      let failureMessage = v.toString()
      let equipmentId = v.LookupRequest.EquipmentOrSerialNumber
      DowngradedForm (Components.MeterReadingsForm.EntryState.init equipmentId failureMessage), Cmd.none
  | EquipmentLookup, EquipmentSelected e ->
    ContactEntry { Equipment = e; Contact = None; }, Cmd.none
  | ContactEntry s, ContactEntryCompleted c ->
    LoadingMeters { LoadingMetersState.Equipment = s.Equipment; Contact = c; }, getMetersCmd s.Equipment.Token
  | LoadingMeters s, LoadingMetersResult r ->
    match r with
    | Ok [] ->
      let failureMessage = "No meters found for equipment"
      let equipmentId = s.Equipment.EquipmentNumber
      DowngradedForm (Components.MeterReadingsForm.EntryState.init equipmentId failureMessage), Cmd.none
    | Ok meters ->
      let m =
        meters |> List.map (fun x ->
          { MeterId = x.MeterId
            MeterType = x.MeterType
            Reading = None
            LastReading = x.LastReading
            LastReadingDate = x.LastReadingDate })
      MeterReadingEntry { Equipment = s.Equipment; SendConfirmationEmail = true; Contact = s.Contact; MeterReadings = m; }, Cmd.none
    | Error msg ->
      let failureMessage = msg
      let equipmentId = s.Equipment.EquipmentNumber
      DowngradedForm (Components.MeterReadingsForm.EntryState.init equipmentId failureMessage), Cmd.none
  | MeterReadingEntry s, UpdateReading newReading ->
    let m = s.MeterReadings |> List.map (fun x -> if x.MeterId = newReading.MeterId then { x with Reading = newReading.Reading } else x)
    MeterReadingEntry { s with MeterReadings = m }, Cmd.none
  | MeterReadingEntry s, UpdateSendConfirmationEmailFlag b ->
    MeterReadingEntry { s with SendConfirmationEmail = b; }, Cmd.none
  | MeterReadingEntry s, SubmitMeterReadings ->
    if s.MeterReadings |> List.exists (fun x -> x.Reading.IsNone)
    then
      let alert =
        Cmd.Swal.fire [
            swal.title "Reading not provided"
            swal.text "All meters require a value." ]
      MeterReadingEntry s, alert
    else
      let m = s.MeterReadings |> List.choose (fun x -> x.Reading |> Option.map (fun r -> { NewMeterReadingRequest.MeterId = x.MeterId; MeterType = x.MeterType; Reading = r; }))
      let r = { Shared.NewMeterReadingRequests.Token = s.Equipment.Token; SendConfirmationEmail = s.SendConfirmationEmail; EquipmentNumber = s.Equipment.EquipmentNumber; Contact = s.Contact; Readings = m }
      SubmittingReadings s, submitMeterReadingsCmd s.Equipment.Token r
  | SubmittingReadings s, MeterReadingEntryResult r ->
    match r with
    | Ok o ->
      let successMessage =
        if s.SendConfirmationEmail
        then "Thank you. Your meter reading(s) have been received, and you will receive an notification when your request has been processed."
        else "Thank you. Your meter reading(s) have been received."
      ReadingRequestResult { SuccessMessage = successMessage; MeterReadingEntryState = s; }, Cmd.none
    | Error msg ->
      let t = sprintf "Thank you. Your request has been queued with reference %s." msg
      ReadingRequestResult { SuccessMessage = t; MeterReadingEntryState = s; }, Cmd.none
  | _ -> model, Cmd.none

let private (|Int|_|) (str: string) =
    match System.Int32.TryParse(str) with
    | true,res -> Some res
    | _ -> None

let meterReadingEntryView (model : MeterReadingEntryState) dispatch =
  Html.div [
    for m in model.MeterReadings do
      Mui.grid [
        grid.container true
        grid.direction.column
        grid.spacing._2
        grid.children [
          Mui.grid [
            grid.item true
            grid.children [
              Html.form [
                prop.children [
                  // match m.LastReading, m.LastReadingDate with
                  // | Some prevRead, Some prevDate ->
                  //   Mui.textField [
                  //     textField.value prevRead
                  //     textField.variant.filled
                  //     textField.margin.normal
                  //     textField.type' "number"
                  //     textField.fullWidth true
                  //     textField.label (sprintf "Previous %s meter reading (%s)" m.MeterType (prevDate.ToShortDateString()))
                  //   ]
                  // | _ -> Html.none
                  Mui.textField [
                    textField.placeholder "New meter reading"
                    textField.value (m.Reading |> Option.map (sprintf "%i") |> Option.defaultValue "")
                    textField.variant.outlined
                    textField.margin.normal
                    textField.type' "number"
                    textField.inputProps [ prop.min 0 ]
                    textField.fullWidth true
                    textField.label (sprintf "New %s meter reading" m.MeterType)
                    textField.required true
                    textField.onChange (fun (ev : Browser.Types.Event) -> { MeterId = m.MeterId; Reading = parseInt (!!ev.target?value) } |> UpdateReading |> dispatch)
                  ]
                ]
              ]
            ]
          ]
        ]
      ]
  ]

let meterReadingView (model : MeterReadingEntryState) =
  Html.div [
    for m in model.MeterReadings do
      Mui.grid [
        grid.container true
        grid.direction.column
        grid.spacing._2
        grid.children [
          Mui.grid [
            grid.item true
            grid.children [
              Html.form [
                prop.children [
                  // match m.LastReading, m.LastReadingDate with
                  // | Some prevRead, Some prevDate ->
                  //   Mui.textField [
                  //     textField.value prevRead
                  //     textField.variant.filled
                  //     textField.margin.normal
                  //     textField.type' "number"
                  //     textField.fullWidth true
                  //     textField.label (sprintf "Previous %s meter reading (%s)" m.MeterType (prevDate.ToShortDateString()))
                  //   ]
                  // | _ -> Html.none
                  Mui.textField [
                    textField.placeholder "New meter reading"
                    textField.value (m.Reading |> Option.map (sprintf "%i") |> Option.defaultValue "")
                    textField.variant.filled
                    textField.margin.normal
                    textField.type' "number"
                    textField.inputProps [ prop.min 0 ]
                    textField.fullWidth true
                    textField.label (sprintf "New %s meter reading" m.MeterType)
                    textField.required true
                  ]
                ]
              ]
            ]
          ]
        ]
      ]
  ]

let viewSendConfirmationEmailToggle (s : MeterReadingEntryState) enabled dispatch =
  Mui.grid [
    grid.container true
    //grid.direction.column
    grid.spacing._2
    grid.children [
      Mui.grid [
        grid.item true
        grid.xs._6
        grid.children [
          Mui.formControl [
            Mui.formLabel [
              Mui.formGroup [
                Mui.formControlLabel [
                  formControlLabel.control (
                    Mui.checkbox [
                      checkbox.checked' s.SendConfirmationEmail
                      checkbox.disabled (not enabled)
                      checkbox.onChange (fun (b : bool) -> UpdateSendConfirmationEmailFlag b |> dispatch)
                      prop.name "Send confirmation email"
                    ]
                  )
                  formControlLabel.label "Send confirmation email"
                ]
              ]
            ]
          ]
        ]
      ]
    ]
  ]

let viewSubmitAndCancelButtons (classes : Styles.SiteClasses) (s : MeterReadingEntryState) isLoading dispatch =
  let buttonProgressIndicator =
    Mui.circularProgress [
      prop.className classes.buttonProgress
      prop.size 24
    ]
  Mui.grid [
    grid.container true
    //grid.direction.column
    grid.spacing._2
    grid.children [
      Mui.grid [
        grid.item true
        grid.xs._6
        grid.children [
          Html.div [
            prop.className classes.wrapper
            prop.children [
              Mui.button [
                prop.type'.submit
                button.fullWidth true
                button.variant.contained
                button.color.primary
                button.children "Submit"
                button.disabled (s.MeterReadings |> List.exists (fun x -> x.Reading.IsNone) || isLoading)
                prop.onClick (fun e ->
                  e.preventDefault()
                  SubmitMeterReadings |> dispatch
                )
              ]
              if isLoading then buttonProgressIndicator else Html.none
            ]
          ]
        ]
      ]
      Mui.grid [
        grid.item true
        grid.xs._6
        grid.children [
          Mui.button [
            button.fullWidth true
            button.variant.contained
            button.color.default'
            button.children "Clear"
            button.disabled isLoading
            prop.onClick (fun e ->
              e.preventDefault()
              ResetState |> dispatch
            )
          ]
        ]
      ]
    ]
  ]

let pendingHeaderMessage =
  Mui.grid [
    grid.container true
    grid.direction.column
    grid.spacing._2
    grid.children [
      Mui.grid [
        grid.item true
        grid.children [
          Mui.typography [
            typography.variant.body1
            typography.children "Please fill out this form to add meter readings."
          ]
        ]
      ]
    ]
  ]

let timelineOppositeContentStyles =
  prop.style [
    style.flexBasis (0.2)
    style.flexGrow (0)
    style.flexShrink (0)
    style.padding (0)
  ]

let viewSectionHeader (headerText : string) =
  Mui.grid [
    grid.container true
    grid.direction.column
    grid.spacing._2
    grid.children [
      Mui.grid [
        grid.item true
        grid.children [
          Mui.typography [
            typography.variant.h5
            typography.gutterBottom false
            typography.children headerText
          ]
        ]
      ]
    ]
  ]

let hideOnMobile (e : ReactElement) =
  Mui.hidden [
    hidden.smDown true
    hidden.children e
  ]

type SectionParts = { Header : ReactElement; IsActive : bool; HideOnMobile : bool; Content : ReactElement list; Icon : ReactElement; }

let equipmentIcon = printerIcon []
let contactIcon = personIcon []
let requestDetailsIcon = gaugeIcon []
let orderIcon = checkOutlineIcon []

let equipmentHeader = viewSectionHeader "Equipment"
let contactHeader = viewSectionHeader "Contact"
let requestDetailsHeader = viewSectionHeader "Meters"
let resultHeader = viewSectionHeader "Request Result"

let buildSections model reCaptchaSiteKey classes dispatch =
  match model with
  | EquipmentLookup ->
    [ { Header = equipmentHeader
        IsActive = true;
        HideOnMobile = false;
        Content = [ Components.EquipmentLookup.view {| reCaptchaSiteKey = reCaptchaSiteKey; equipmentOrSerialNumberOption = None; returnResult = (fun e -> e |> EquipmentLookupResult |> dispatch) |} ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = contactIcon }
      { Header = requestDetailsHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = requestDetailsIcon }
      { Header = resultHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderIcon }  ]
  | ContactEntry s ->
    [ { Header = equipmentHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.EquipmentInfo.view s.Equipment ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = true;
        HideOnMobile = false;
        Content =
          match s.Contact with
          | None -> [ Components.ContactLookup.view {| token = s.Equipment.Token; returnResult = ContactEntryCompleted >> dispatch; |} ]
          | Some c -> [ Components.ContactInfo.view c ]
        Icon = contactIcon }
      { Header = requestDetailsHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = requestDetailsIcon }
      { Header = resultHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderIcon }  ]
  | LoadingMeters s ->
    [ { Header = equipmentHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.EquipmentInfo.view s.Equipment ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = contactIcon }
      { Header = requestDetailsHeader
        IsActive = false;
        HideOnMobile = true;
        Content = [] // TODO Loading indicator
        Icon = requestDetailsIcon }
      { Header = resultHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderIcon } ]
  | MeterReadingEntry s ->
    [ { Header = equipmentHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.EquipmentInfo.view s.Equipment ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.ContactInfo.view s.Contact ]
        Icon = contactIcon }
      { Header = requestDetailsHeader
        IsActive = true;
        HideOnMobile = false;
        Content =
          [ meterReadingEntryView s dispatch
            viewSendConfirmationEmailToggle s true dispatch
            viewSubmitAndCancelButtons classes s false dispatch]
        Icon = requestDetailsIcon }
      { Header = resultHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderIcon } ]
  | SubmittingReadings s ->
    [ { Header = equipmentHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.EquipmentInfo.view s.Equipment ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.ContactInfo.view s.Contact ]
        Icon = contactIcon }
      { Header = requestDetailsHeader
        IsActive = true;
        HideOnMobile = false;
        Content =
          [ meterReadingView s
            viewSendConfirmationEmailToggle s false dispatch
            viewSubmitAndCancelButtons classes s true dispatch ]
        Icon = requestDetailsIcon }
      { Header = resultHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderIcon } ]
  | ReadingRequestResult r ->
    [ { Header = equipmentHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.EquipmentInfo.view r.MeterReadingEntryState.Equipment ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.ContactInfo.view r.MeterReadingEntryState.Contact ]
        Icon = contactIcon }
      { Header = requestDetailsHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [
          meterReadingView r.MeterReadingEntryState
          viewSendConfirmationEmailToggle r.MeterReadingEntryState true dispatch ]
        Icon = requestDetailsIcon }
      { Header = resultHeader
        IsActive = false;
        HideOnMobile = false;
        Content =
          [ Html.text r.SuccessMessage
            Mui.button [
              prop.type'.submit
              button.fullWidth true
              button.variant.contained
              button.color.primary
              button.children "Add Another Reading"
              prop.onClick (fun e ->
                e.preventDefault ()
                ResetState |> dispatch
              )
            ]
          ]
        Icon = orderIcon } ]
  | DowngradedForm _ ->
    []

let viewMobile (sections : SectionParts list) =
  Html.div [
    prop.children (
      sections
      |> List.filter (fun x -> not x.HideOnMobile)
      |> List.collect (fun x ->
        [
          x.Header
          yield! x.Content
        ]
      )
    )
  ]

let viewNonMobile (sections : SectionParts list) =
  Mui.timeline [
    timeline.children (
      sections
      |> List.mapi (fun i x ->
        Mui.timelineItem [
          Mui.timelineOppositeContent [
            //prop.className classes.timelineOppositeContent
            timelineOppositeContentStyles
          ]
          Mui.timelineSeparator [
            Mui.timelineDot [
              if x.IsActive then timelineDot.color.primary
              timelineDot.children [ x.Icon ]
            ]
            if (i + 1) < sections.Length then
              Mui.timelineConnector [
              ]
          ]
          Mui.timelineContent [
            x.Header
            yield! x.Content
          ]
        ]
      )
    )
  ]

let view = React.functionComponent (fun (inputProps: {| equipmentOption : Equipment option; reCaptchaSiteKey : string; |}) ->
  let model, dispatch = React.useElmish(init (inputProps.equipmentOption), update, [| |])
  let classes = Styles.useStyles()
  let isMobile = Hooks.useMediaQuery (Client.Theme.defaultTheme.breakpoints.downSm)
  Html.div [
    prop.className [
      classes.standAloneContent
    ]
    prop.children [
      Mui.grid [
        grid.container true
        grid.direction.column
        grid.spacing._2
        grid.children [
          Mui.grid [
            grid.item true
            grid.children [
              Mui.typography [
                typography.variant.h4
                typography.children "Add Meter Reading(s)"
              ]
            ]
          ]
        ]
      ]

      match model with
      | DowngradedForm initialState -> Components.MeterReadingsForm.view {| reCaptchaSiteKey = inputProps.reCaptchaSiteKey; initialValues = Some initialState ; returnResult = (fun _ -> dispatch ResetState); |}
      | _ ->
        pendingHeaderMessage
        let sections = buildSections model inputProps.reCaptchaSiteKey classes dispatch
        match isMobile with
        | true -> viewMobile sections
        | false -> viewNonMobile sections
    ]
  ]
)